import { z } from 'zod';
import { isoDateTimeSchema } from '../common/coercion.js';
import { messageCorrelationIdSchema } from '../common/correlation.js';
import { flowIdSchema, systemSchema } from '../common/systems.js';
import { flowNodeStatusSchema } from './flow-graph.js';
import { paginatedSchema, paginationQuerySchema } from './monitoring.js';
/**
 * Corridas: el message log agrupado por correlation id.
 *
 * QUE RESUELVE: el listado de mensajes tiene una fila por mensaje y una sola
 * corrida de F8 deja miles. Para encontrar "la corrida de ayer de F8" habia que
 * hojear paginas de mensajes. Una corrida es una fila aca, y desde ella se abre
 * el grafo, que ya existia.
 *
 * QUE ES UNA CORRIDA: todo lo que comparte un correlation id. En los flujos por
 * lote (F3, F8, F12) es una ejecucion completa; en los que parten por webhook
 * (F1, Multivende) es UNA entidad —un pedido—, y por eso el filtro de texto
 * sobre el correlation id importa tanto como el de flujo.
 *
 * LO QUE QUEDA FUERA: los hilos sin flujo (logins de SAP y Syncro, handshakes de
 * webhooks). Son plomeria, no corridas de negocio, y siguen visibles en el
 * listado de mensajes.
 */
/**
 * Estados por los que se puede filtrar.
 *
 * Los mismos que el grafo le pone a la corrida entera, menos `pending`: una
 * corrida que existe tiene al menos un mensaje, asi que nunca esta pendiente.
 */
export const flowRunStatusSchema = flowNodeStatusSchema.exclude(['pending']);
/** Una corrida en el listado. */
export const flowRunSummarySchema = z.object({
    correlationId: messageCorrelationIdSchema,
    /** Flujo con mas mensajes en la corrida, igual que en el grafo. */
    flow: flowIdSchema.nullable(),
    /**
     * Todos los flujos que aparecen en la corrida, en orden numerico (F2 antes
     * que F10).
     *
     * Una corrida de F12 puede arrastrar pasos de F13: quien filtra por F13 tiene
     * que ver por que esta corrida aparece aunque se informe como de F12.
     */
    flows: z.array(flowIdSchema),
    /**
     * Agregado con `aggregateFlowNodeStatus`, las mismas reglas que un paso. Nunca
     * `pending`: una corrida que existe tiene al menos un mensaje.
     */
    status: flowRunStatusSchema,
    /** Su primer mensaje, de cualquier flujo: el mismo con el que se ordena y filtra. */
    startedAt: isoDateTimeSchema,
    /** Ultima actividad: lo mas tardio entre recepcion y procesamiento. */
    endedAt: isoDateTimeSchema,
    durationMs: z.number().int().nonnegative(),
    messageCount: z.number().int().positive(),
    processedCount: z.number().int().nonnegative(),
    /** `failed` o `dead`. La cuarentena va aparte, como en el grafo. */
    failedCount: z.number().int().nonnegative(),
    quarantinedCount: z.number().int().nonnegative(),
    /** Recibidos, encolados o procesandose. */
    runningCount: z.number().int().nonnegative(),
    /**
     * Primer mensaje de la corrida: dice que la inicio (un webhook, una
     * reconciliacion, una corrida programada) sin que la API lo interprete.
     */
    firstSystem: systemSchema,
    firstEventType: z.string().nullable(),
    /** El error mas reciente de la corrida, para verlo sin abrirla. */
    lastError: z.string().nullable(),
});
/**
 * Filtros del listado de corridas.
 *
 * Igual que en mensajes, un filtro desconocido se rechaza en vez de ignorarse.
 */
export const flowRunFilterSchema = paginationQuerySchema.extend({
    /** Corridas con al menos un mensaje de este flujo. */
    flow: flowIdSchema.optional(),
    status: flowRunStatusSchema.optional(),
    /**
     * Parte del correlation id, sin distinguir mayusculas, o el numero exacto de
     * un pedido (el `externalId` del POS, que es el `commerceId` de Janis; el id
     * de Janis o el de Multivende). Un pedido abre varios hilos (el alta del POS,
     * los avisos de Janis, el DTE): con su numero aparecen todos.
     */
    correlationId: z.string().trim().min(1).max(128).optional(),
    /** Rango sobre el inicio de la corrida (su primer mensaje), en ISO UTC. */
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
export const flowRunListResponseSchema = paginatedSchema(flowRunSummarySchema);
/** Tope de vecinas por lado: la tira del grafo no necesita mas. */
export const FLOW_RUN_NEIGHBORS_MAX = 50;
export const flowRunNeighborsQuerySchema = z.object({
    /** Cuantas corridas traer a cada lado de la actual. */
    limit: z.coerce.number().int().positive().max(FLOW_RUN_NEIGHBORS_MAX).default(10),
});
/**
 * Las corridas anteriores y siguientes del mismo flujo.
 *
 * Es lo que permite, desde el grafo de una corrida, saltar a la de ayer sin
 * volver al listado. "Mismo flujo" es el flujo dominante de la corrida actual.
 */
export const flowRunNeighborsSchema = z.object({
    correlationId: messageCorrelationIdSchema,
    /** `null` si la corrida no declara flujo: entonces no hay vecinas. */
    flow: flowIdSchema.nullable(),
    /** La corrida actual, o `null` si el correlation id no tiene mensajes. */
    current: flowRunSummarySchema.nullable(),
    /** De la mas reciente a la mas antigua: `older[0]` es la inmediatamente anterior. */
    older: z.array(flowRunSummarySchema),
    /** De la mas antigua a la mas reciente: `newer[0]` es la inmediatamente siguiente. */
    newer: z.array(flowRunSummarySchema),
});
//# sourceMappingURL=flow-runs.js.map